<template>
  <div id="app">
    <FloatingHeader />

    <div class="main-content">
      <FeedbackAdmin v-if="page === 'feedback-admin'" />
      <FeedbackBoard v-else-if="page === 'feedback'" />

      <template v-else>
        <NewInstance v-if="!instanceId" :createInstance="createInstance" />

        <NameEntry v-if="instanceId && !name && instanceData" :addName="addName" />

        <ThingBoard
          v-if="instanceData && name"
          :instanceData="instanceData"
          :name="name"
          :instanceId="instanceId"
          :addThing="addThing"
          :editThing="editThing"
          :removeThing="removeThing"
          :saveRatings="saveRatings"
        />
      </template>

      <div v-if="page === 'app' && error" class="error">
        {{ error }}
      </div>

      <loading-overlay v-if="page === 'app'" :loading="loading" />
    </div>
  </div>
</template>

<script>
import FloatingHeader from "@/components/FloatingHeader.vue";
import NewInstance from "@/components/NewInstance.vue";
import NameEntry from "@/components/NameEntry.vue";
import ThingBoard from "@/components/ThingBoard.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import FeedbackBoard from "@/components/FeedbackBoard.vue";
import FeedbackAdmin from "@/components/FeedbackAdmin.vue";

const feedbackPageFromPath = () => {
  const path = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/feedback/admin") return "feedback-admin";
  if (path === "/feedback") return "feedback";
  return "app";
};

export default {
  name: "App",
  components: {
    FloatingHeader,
    NewInstance,
    NameEntry,
    ThingBoard,
    LoadingOverlay,
    FeedbackBoard,
    FeedbackAdmin
  },
  data() {
    return {
      page: feedbackPageFromPath(),
      instanceId: null,
      instanceData: null,
      name: null,
      loading: false,
      error: null,
      socket: null,
      reconnectTimeout: null,
      isConnecting: false,
      wsUrl: null,
      idleTimeout: null,
      idleDisconnected: false
    };
  },
  mounted() {
    if (this.page !== "app") {
      return;
    }

    // Use WebSocket subdomain (bypasses CloudFront, goes directly to API Gateway)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Replace any existing subdomain with 'ws', or prepend 'ws.' if no subdomain
    const host = window.location.host;
    const isLocal = host.includes('localhost') || host.startsWith('127.0.0.1');
    if (isLocal) {
      this.wsUrl = 'wss://ws.pickything.com';
    } else {
      const hostParts = host.split('.');
      const wsHost = hostParts.length > 2
        ? `ws.${hostParts.slice(1).join('.')}`
        : `ws.${host}`;
      this.wsUrl = `${protocol}//${wsHost}`;
    }
    console.log('WebSocket URL configured:', this.wsUrl);

    const pathParts = window.location.pathname.split("/");
    this.instanceId = pathParts[pathParts.length - 1];

    if (this.instanceId) {
      this.fetchInstanceData();
      
      const lsName = localStorage.getItem(this.instanceId + "name");
      if (lsName) {
        this.name = JSON.parse(lsName);
      }
    }

    // Set up idle timeout monitoring
    this.setupIdleTimeout();
    // Listen for user interactions to reset idle timer
    this.setupActivityListeners();
  },
  beforeUnmount() {
    this.clearIdleTimeout();
    this.removeActivityListeners();
    this.disconnectWebSocket();
  },
  methods: {
    connectWebSocket() {
      if (this.socket || !this.instanceId) return;
      if (!this.wsUrl) {
        console.error("WebSocket URL not configured yet");
        return;
      }

      console.log("Connecting to WebSocket:", this.wsUrl);
      this.socket = new WebSocket(this.wsUrl);

      this.socket.onopen = () => {
        console.log("WebSocket Connected");
        this.idleDisconnected = false; // Reset idle disconnect flag
        this.socket.send(JSON.stringify({ 
          action: 'join', 
          instanceId: this.instanceId,
          userId: this.name?.id
        }));
        // Reset idle timeout after successful connection
        this.resetIdleTimeout();
      };

      this.socket.onmessage = (event) => {
        // Reset idle timeout on any message (activity from server)
        this.resetIdleTimeout();
        
        const message = JSON.parse(event.data);
        if (message.type === 'UPDATE') {
          console.log("State updated via WebSocket:", message.data);
          // Merge server data with local state to preserve optimistic updates
          if (this.instanceData) {
            this.instanceData = { ...this.instanceData, ...message.data };
          } else {
            this.instanceData = message.data;
          }
        }
      };

      this.socket.onclose = () => {
        console.log("WebSocket Disconnected");
        this.socket = null;
        if (!this.idleDisconnected) {
          this.reconnectTimeout = setTimeout(() => this.connectWebSocket(), 3000);
        }
      };

      this.socket.onerror = (err) => {
        console.error("WebSocket Error:", err);
      };
    },
    disconnectWebSocket() {
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
      }
    },
    async makeRequest(method, path, jsonBody, skipLoad) {
      this.loading = !skipLoad;
      this.error = null;

      try {
        let body = jsonBody ? JSON.stringify(jsonBody) : null;
        // Use relative path for CloudFront routing (/api/...)
        const response = await fetch("/api/" + path, {
          method: method,
          headers: { "Content-Type": "application/json" },
          body: body,
        });

        if (response.ok) {
          return await response.json();
        } else {
          this.error = response.statusText;
        }
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    sendSocketMessage(action, payload = {}) {
      // Reset idle timer on any interaction
      this.resetIdleTimeout();
      
      // If we disconnected due to idle, reconnect now
      if (this.idleDisconnected) {
        console.log("Reconnecting after idle disconnect...");
        this.idleDisconnected = false;
        this.connectWebSocket();
        // Wait a moment for connection to establish, then send message
        setTimeout(() => {
          if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({
              action,
              instanceId: this.instanceId,
              payload
            }));
          }
        }, 500);
        return;
      }
      
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({
          action,
          instanceId: this.instanceId,
          payload
        }));
      } else {
        console.warn("Socket not ready, attempting to reconnect...");
        this.connectWebSocket();
      }
    },
    async createInstance(description, name) {
      const responseData = await this.makeRequest("POST", "create", { description, name });

      if (responseData) {
        this.instanceId = responseData.id;
        this.instanceData = responseData;
        this.name = responseData.names[0];
        localStorage.setItem(this.instanceId + "name", JSON.stringify(this.name));

        const newPath = window.location.pathname.replace(/\/$/, "") + `/${this.instanceId}`;
        window.history.pushState({}, "", newPath);
        
        this.connectWebSocket();
      }
    },
    async fetchInstanceData() {
      const responseData = await this.makeRequest("GET", "instance/" + this.instanceId);
      if (responseData && !responseData.error) {
        this.instanceData = responseData;
        if (this.name) {
          this.connectWebSocket();
        }
      } else if (responseData?.error) {
        this.error = responseData.error;
      }
    },
    async addName(name) {
      const responseData = await this.makeRequest("POST", `instance/${this.instanceId}/name`, { name });
      if (responseData) {
        this.name = responseData;
        localStorage.setItem(this.instanceId + "name", JSON.stringify(this.name));
        // Now that we have a name, fetch updated instance data and connect WebSocket
        await this.fetchInstanceData();
        this.connectWebSocket();
      }
    },
    async addThing(newThing) {
      this.sendSocketMessage('addThing', { thing: newThing, userId: this.name.id });
    },
    async editThing(thingId, newThing) {
      this.sendSocketMessage('editThing', { id: thingId, thing: newThing, userId: this.name.id });
    },
    async removeThing(thingId) {
      this.sendSocketMessage('removeThing', { id: thingId, userId: this.name.id });
    },
    async saveRatings(ratings, vetos) {
      this.sendSocketMessage('saveRatings', { userId: this.name.id, ratings, vetos });
    },
    setupIdleTimeout() {
      // 5 minutes = 300000 milliseconds
      this.resetIdleTimeout();
    },
    resetIdleTimeout() {
      // Clear existing timeout
      this.clearIdleTimeout();
      
      // Only set timeout if we have an active connection and not in final state
      if (this.socket) {
        this.idleTimeout = setTimeout(() => {
          console.log("Idle timeout reached, disconnecting WebSocket...");
          this.idleDisconnected = true;
          this.disconnectWebSocket();
        }, 300000); // 5 minutes
      }
    },
    clearIdleTimeout() {
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
        this.idleTimeout = null;
      }
    },
    setupActivityListeners() {
      // Listen for various user interactions
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.addEventListener(event, this.resetIdleTimeout, true);
      });
    },
    removeActivityListeners() {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.removeEventListener(event, this.resetIdleTimeout, true);
      });
    },
  },
};
</script>

<style>
*,
*::before,
*::after {
  box-sizing: border-box;
}

#app {
  font-family: Roboto, sans-serif;
}

html,
body,
div,
span,
applet,
object,
iframe,
h1,
h2,
h3,
h4,
h5,
h6,
p,
blockquote,
pre,
a,
abbr,
acronym,
address,
big,
cite,
code,
del,
dfn,
em,
img,
ins,
kbd,
q,
s,
samp,
small,
strike,
strong,
sub,
sup,
tt,
var,
b,
u,
i,
center,
dl,
dt,
dd,
ol,
ul,
li,
fieldset,
form,
label,
legend,
table,
caption,
tbody,
tfoot,
thead,
tr,
th,
td,
article,
aside,
canvas,
details,
embed,
figure,
figcaption,
footer,
header,
hgroup,
menu,
nav,
output,
ruby,
section,
summary,
time,
mark,
audio,
video {
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}

/* HTML5 display-role reset for older browsers */
article,
aside,
details,
figcaption,
figure,
footer,
header,
hgroup,
menu,
nav,
section {
  display: block;
}

body {
  line-height: 1;
}

ol,
ul {
  list-style: none;
}

blockquote,
q {
  quotes: none;
}

blockquote:before,
blockquote:after,
q:before,
q:after {
  content: "";
  content: none;
}

table {
  border-collapse: collapse;
  border-spacing: 0;
}

.centered-container {
  max-width: 600px;
  margin: 50px auto;
  padding: 0 12px;
  text-align: center;
  box-sizing: border-box;
}

.promo-heading {
  font-style: italic;
  font-weight: normal;
  line-height: 1.4;
}

.sub-heading {
  font-weight: bold;
  margin-top: 10px;
  line-height: 1.3;
}

.content-box {
  margin-top: 20px;
  border: none;
  background-color: #fff;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
  display: block;
}

.box-heading {
  padding: 10px;
  background-color: #3498db;
  /* Blue background color */
  color: #fff;
  /* White text color */
  text-align: left;
  margin-top: 0;
}

.box-content {
  padding: 15px;
  margin-top: 0;
}

.input-container {
  margin-top: 10px;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
}

button {
  padding: 10px;
  background-color: #3498db;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.state {
  padding: 5px;
  background-color: #3498db;
  /* Blue button color */
  color: #fff;
  /* White text color */
  border: none;
  border-radius: 3px;
}

.actions button {
  padding: 5px;
  border-radius: 3px;
}

.actions img {
  height:24px;
  width: 24px;
}
.finish-button {
  margin-top: 20px;
}

.finish-button:disabled {
  background-color: #ccc;
  /* Use a different background color for disabled state */
  cursor: not-allowed;
  /* Change cursor to not-allowed for disabled state */
}

.main-content {
  padding: 20px;
}

@media (max-width: 600px) {
  .centered-container {
    margin: 40px auto 16px;
    padding: 0 8px;
  }

  .main-content {
    padding: 12px;
  }

  .finish-button {
    width: 100%;
    margin-left: 0;
    min-height: 44px;
  }
}

.error {
  margin-top: 10px;
}

.name-list {
  margin-top:10px;
}

.name-list span {
  padding-right:5px;
}

</style>

